import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid } from '../cacheUtils';

export const fetchWorkspaceDetail = createAsyncThunk(
  'workspaceDetail/fetchDetail',
  async (id, { getState, rejectWithValue }) => {
    const state = getState().workspaceDetail;
    if (isCacheValid(state.lastFetched[id])) {
      return { data: state.details[id], id, cached: true };
    }
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${id}`);
      return { data: response.data.data, id, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspace detail');
    }
  }
);

export const fetchWorkspaceUsers = createAsyncThunk(
  'workspaceDetail/fetchUsers',
  async ({ workspaceId, page = 1, limit = 25 }, { getState, rejectWithValue }) => {
    const cacheKey = `${workspaceId}_users_${page}_${limit}`;
    const state = getState().workspaceDetail;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.users[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/users?page=${page}&limit=${limit}`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspace users');
    }
  }
);

export const fetchWorkspaceEmployees = createAsyncThunk(
  'workspaceDetail/fetchEmployees',
  async ({ workspaceId, page = 1, limit = 25 }, { getState, rejectWithValue }) => {
    const cacheKey = `${workspaceId}_employees_${page}_${limit}`;
    const state = getState().workspaceDetail;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.employees[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/employees?page=${page}&limit=${limit}`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspace employees');
    }
  }
);

export const fetchWorkspaceBilling = createAsyncThunk(
  'workspaceDetail/fetchBilling',
  async (workspaceId, { getState, rejectWithValue }) => {
    const cacheKey = `${workspaceId}_billing`;
    const state = getState().workspaceDetail;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.billing[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/billing`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspace billing');
    }
  }
);

export const fetchWorkspaceFeatures = createAsyncThunk(
  'workspaceDetail/fetchFeatures',
  async (workspaceId, { getState, rejectWithValue }) => {
    const cacheKey = `${workspaceId}_features`;
    const state = getState().workspaceDetail;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.features[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/features`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspace features');
    }
  }
);

const workspaceDetailSlice = createSlice({
  name: 'workspaceDetail',
  initialState: {
    details: {},
    users: {},
    employees: {},
    billing: {},
    features: {},
    loading: {
      detail: false,
      users: false,
      employees: false,
      billing: false,
      features: false,
    },
    error: null,
    lastFetched: {},
  },
  reducers: {
    invalidateCache: (state, action) => {
      if (action.payload) {
        // Invalidate specific workspace
        const id = action.payload;
        Object.keys(state.lastFetched).forEach(key => {
          if (key.startsWith(id)) {
            delete state.lastFetched[key];
          }
        });
        delete state.lastFetched[id];
      } else {
        state.lastFetched = {};
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Detail
      .addCase(fetchWorkspaceDetail.pending, (state) => {
        state.loading.detail = true;
      })
      .addCase(fetchWorkspaceDetail.fulfilled, (state, action) => {
        const { data, id, cached } = action.payload;
        if (!cached) {
          state.details[id] = data;
          state.lastFetched[id] = Date.now();
        }
        state.loading.detail = false;
      })
      .addCase(fetchWorkspaceDetail.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload;
      })
      // Users
      .addCase(fetchWorkspaceUsers.pending, (state) => {
        state.loading.users = true;
      })
      .addCase(fetchWorkspaceUsers.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.users[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading.users = false;
      })
      .addCase(fetchWorkspaceUsers.rejected, (state, action) => {
        state.loading.users = false;
        state.error = action.payload;
      })
      // Employees
      .addCase(fetchWorkspaceEmployees.pending, (state) => {
        state.loading.employees = true;
      })
      .addCase(fetchWorkspaceEmployees.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.employees[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading.employees = false;
      })
      .addCase(fetchWorkspaceEmployees.rejected, (state, action) => {
        state.loading.employees = false;
        state.error = action.payload;
      })
      // Billing
      .addCase(fetchWorkspaceBilling.pending, (state) => {
        state.loading.billing = true;
      })
      .addCase(fetchWorkspaceBilling.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.billing[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading.billing = false;
      })
      .addCase(fetchWorkspaceBilling.rejected, (state, action) => {
        state.loading.billing = false;
        state.error = action.payload;
      })
      // Features
      .addCase(fetchWorkspaceFeatures.pending, (state) => {
        state.loading.features = true;
      })
      .addCase(fetchWorkspaceFeatures.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.features[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading.features = false;
      })
      .addCase(fetchWorkspaceFeatures.rejected, (state, action) => {
        state.loading.features = false;
        state.error = action.payload;
      });
  },
});

export const { invalidateCache: invalidateWorkspaceDetailCache } = workspaceDetailSlice.actions;
export default workspaceDetailSlice.reducer;